package link.erpango.quip.theme

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.toArgb

class QuipColors {
    companion object {
        private val defaultScheme = ColorSchemes.ClassicMechanic

        // Referencia al esquema actual para poder acceder a todos sus valores
        var currentScheme: QuipColorScheme by mutableStateOf(defaultScheme)
            private set

        var colorPrimary by mutableStateOf(defaultScheme.colorPrimary)
        var colorSecondary by mutableStateOf(defaultScheme.colorSecondary)
        var colorOnPrimary by mutableStateOf(defaultScheme.colorOnPrimary)
        var colorBackground by mutableStateOf(defaultScheme.colorBackground)
        var colorSurface by mutableStateOf(defaultScheme.colorSurface)
        var colorText by mutableStateOf(defaultScheme.colorText)
        var colorTextSecondary by mutableStateOf(defaultScheme.colorTextSecondary)

        // Gradients
        var gradientStart by mutableStateOf(defaultScheme.gradientStart)
        var gradientEnd by mutableStateOf(defaultScheme.gradientEnd)

        // Colores para los mensajes Snackbar
        var colorError by mutableStateOf(defaultScheme.colorError)
        var colorSuccess by mutableStateOf(defaultScheme.colorSuccess)
        var colorWarning by mutableStateOf(defaultScheme.colorWarning)
        var white by mutableStateOf(defaultScheme.white)
        var black by mutableStateOf(defaultScheme.black)
        
        // Lista de listeners para notificar a las plataformas nativas cuando cambian los colores
        // Parámetros: backgroundColorArgb, isLightBackground (true = usar iconos oscuros)
        private val onColorsChangedListeners = mutableListOf<(backgroundColorArgb: Int, isLightBackground: Boolean) -> Unit>()
        
        /**
         * Añade un listener para notificar cuando cambian los colores.
         * Usado por Android/iOS para actualizar la barra de estado/navegación.
         * @param listener Recibe el color ARGB y si es un fondo claro (para decidir color de iconos)
         * @return El listener añadido (para poder eliminarlo después)
         */
        fun addOnColorsChangedListener(listener: (backgroundColorArgb: Int, isLightBackground: Boolean) -> Unit): (Int, Boolean) -> Unit {
            onColorsChangedListeners.add(listener)
            // Notificar inmediatamente con el color actual
            val bgColor = colorBackground.toArgb()
            listener.invoke(bgColor, isColorLight(bgColor))
            return listener
        }
        
        /**
         * Elimina un listener previamente añadido.
         */
        fun removeOnColorsChangedListener(listener: (backgroundColorArgb: Int, isLightBackground: Boolean) -> Unit) {
            onColorsChangedListeners.remove(listener)
        }
        
        /**
         * Obtiene el color de fondo actual como valor ARGB (para uso en plataformas nativas).
         */
        fun getBackgroundColorArgb(): Int = colorBackground.toArgb()
        
        /**
         * Determina si el color de fondo actual es claro (para usar iconos oscuros en la status bar).
         */
        fun isBackgroundLight(): Boolean = isColorLight(colorBackground.toArgb())
        
        /**
         * Calcula si un color es claro u oscuro usando la fórmula de luminancia relativa.
         * @return true si el color es claro (debería usar texto/iconos oscuros)
         */
        private fun isColorLight(colorArgb: Int): Boolean {
            val red = (colorArgb shr 16) and 0xFF
            val green = (colorArgb shr 8) and 0xFF
            val blue = colorArgb and 0xFF
            
            // Fórmula de luminancia relativa (estándar W3C)
            val luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255
            return luminance > 0.5
        }
        
        /**
         * Actualiza el esquema de colores actual.
         * Debe llamarse cuando el usuario cambia el tema.
         */
        fun updateScheme(scheme: QuipColorScheme) {
            currentScheme = scheme
            colorPrimary = scheme.colorPrimary
            colorSecondary = scheme.colorSecondary
            colorOnPrimary = scheme.colorOnPrimary
            colorBackground = scheme.colorBackground
            colorSurface = scheme.colorSurface
            colorText = scheme.colorText
            colorTextSecondary = scheme.colorTextSecondary
            gradientStart = scheme.gradientStart
            gradientEnd = scheme.gradientEnd
            colorError = scheme.colorError
            colorSuccess = scheme.colorSuccess
            colorWarning = scheme.colorWarning
            white = scheme.white
            black = scheme.black
            
            // Notificar a todas las plataformas del cambio de color de fondo
            val bgColor = colorBackground.toArgb()
            val isLight = isColorLight(bgColor)
            onColorsChangedListeners.forEach { it.invoke(bgColor, isLight) }
        }
    }
}
